export const device = {
	pageTitle: "{{deviceId}}",
	pageSubtitle: "Device",
	liveTelemetryToggle: "Live telemetry",
	loading: "Fetching Device Data",
	actions: {
		config: "Update Config",
		valveControl: "Switch Valve",
		attributes: "Edit Attributes",
		assignTenant: "Assign Tenants",
		delete: "Delete",
	},
	dialog: {
		toggleRelay: {
			loading: "Sending Relay Toggle Request",
			header: "Confirm Toggle Relay",
			message:
				"Please confirm you would like to toggle the relay for device <b>{{device}}</b> from <b>{{from}}</b> to <b>{{to}}</b>.",
			accept: "Toggle Relay",
		},
		switchValve: {
			loading: "Sending Valve Switch Request",
			header: "Confirm Switch Valve",
			message:
				"Please confirm you would like to switch the valve for device <b>{{device}}</b> from <b>{{from}}</b> to <b>{{to}}</b>.",
			accept: "Switch Valve",
		},
		config: {
			loading: "Requesting Config Update",
			header: "Request Config Update",
			save: "Send Request",
			form: {
				wake: {
					label: "Wake Interval",
					max: "Max value for interval type '$t(device.config.interval.type.{{type}})' is {{value}}",
				},
				keepalive: {
					label: "Keep Alive Interval",
					max: "Max value for interval type '$t(device.config.interval.type.{{type}})' is {{value}}",
				},
				intervalType: {
					label: "Interval Type",
				},
			},
		},
		attributes: {
			loading: "Updating Device Attributes",
			header: "Update Device Attributes",
			save: "Update",
			form: {
				name: {
					label: "Device Name",
					maxLength: "Device Name cannot be longer than {{length}}",
				},
				inputDevice: {
					label: "Input Device",
				},
				outputDevice: {
					label: "Output Device",
				},
				sensorType: {
					label: "Sensor Type",
				},
				actuatorType: {
					label: "Actuator Type",
				},
			},
			location: "Device Location",
			toast: {
				successSummary: "Device Update Success",
				successDetail: "",
				errorSummary: "Failed to Update Device",
				errorDetail: "{{error}}",
			},
		},
		alarmGroup: {
			header: "Connected $t(device.feature.{{type}}) Alarm Groups",
		},
		tenants: {
			header: "Assigned Tenants",
		},
		delete: {
			header: "Confirm Delete Device",
			message: "Are you sure you want to delete device <b>{{deviceName}}</b>? This cannot be undone.",
			inAlarmWarn: "This device is currently in an alarm state.",
			alarmGroupWarn_one:
				"This device is currently acting as an {{type}} in an Alarm Group. It will be removed from the group on delete.",
			alarmGroupWarn_other:
				"This device is currently acting as an {{type}} in {{count}} Alarm Groups. It will be removed from these groups on delete.",
		},
		deleteDisabled: {
			header: "Delete Disabled",
			message_INPUT: "Cannot delete this device. It is the last input in groups {{groups}}",
			message_OUTPUT: "Cannot delete this device. It is the last output in groups {{groups}}",
		},
	},
	state: {
		header: "Live State",
		id: "Device Cloud ID",
		flowstops: "FlowStops",
		triggers: "Triggers",
		disabled: "Disabled",
		inAlarmBanner: "This device is in an alarm state",
		clearBanner: "This device has no active alarms",
		lastSeen: "Last Seen",
		status: "Status",
		recordUpdatedAt: "Record Updated At",
		location: "Location",
		noLocation: "No location assigned",
		features: "Connected Features",
		firmware: "Firmware Version",
		hardware: "Hardware Version",
		timestamp: "Device Timestamp",
		relay: "Relay",
		valve: "Valve",
		sensorState: "Sensor State",
		sensorResistance: "Sensor Resistance",
		dcStatus: "DC Power Status",
		dcVoltage: "DC Power Voltage",
		battCharging: "Battery Charging",
		battPercentage: "Battery Percentage",
		alarmGroupsCount_one: "{{count}} Group as $t(device.feature.{{type}})",
		alarmGroupsCount_other: "{{count}} Groups as $t(device.feature.{{type}})",
		tenants: "Tenants",
		tenants_other: "{{count}} Tenants",
		inputDevice: "Input Device",
		outputDevice: "Output Device",
		sensorType: "Sensor Type",
		actuatorType: "Actuator Type",
		wifiSsid: "WiFi SSID",
		wifiRssi: "WiFi RSSI",
		wifiRssiQuality: {
			excellent: "Excellent",
			good: "Good",
			weak: "Weak",
			poor: "Poor",
		},
		presence: {
			online: "Online",
			offline: "Offline",
			offlineWarning:
				"{{family}} appears offline. Last report was {{lastSeen}}. Live state values below are stale after {{staleMinutes}} minutes and are shown for reference.",
		},
		offlineSince: "Offline Since",
		presenceEstimated: "Estimated from the device's reporting interval",
		silentWithoutEvent: "No report for longer than expected, and no connection-lost event was raised",
		silentWithoutEventDetail:
			"This device has not reported since {{lastSeen}}, but no connection-lost event was raised. The offline state shown here follows the connection-lost event, so it may be behind.",
	},
	config: {
		header: "Config",
		wake: "Wake Interval",
		keep: "Keep Alive Interval",
		batt: "Battery Enabled",
		vdc: "VDC Enabled",
		modbus: "Modbus Enabled",
		interval: {
			wakeInterval: "Wake Interval",
			keepInterval: "Keep Alive Interval",
			type: {
				second: "seconds",
				minute: "minutes",
				hour: "hours",
			},
		},
		sections: {
			valves: "Config - Valves",
			outputs: "Config - Outputs",
			alarmsMaintenance: "Config - Alarms and Maintenance",
		},
	},
	modbus: {
		header: "Modbus",
		status: "Status",
		distance: "Distance",
		timestamp: "Timestamp",
		events: "N Events",
	},
	unit: {
		seconds: "seconds",
	},
	sensor: {
		status: {
			on: "Leak",
			off: "Clear",
		},
	},
	relay: {
		on: "On",
		off: "Off",
		open: "Open",
		closed: "Closed",
	},
	dcStatus: {
		true: "Connected",
		false: "Disconnected",
	},
	batteryCharging: {
		true: "Yes",
		false: "No",
	},
	feature: {
		none: "No Connected Devices",
		null: "None",
		input: "Input",
		output: "Output",
		flowstop: "FlowStop",
		flexipad: "Leak Sensor",
		ranger: "Ranger",
		disabled_one: "Cannot disable $t(device.feature.{{type}}) feature as it is part of {{count}} Alarm Group",
		disabled_other: "Cannot disable $t(device.feature.{{type}}) feature as it is part of {{count}} Alarm Groups",
	},
	pendingUpdate:
		"There is a pending update to <b>{{label}}</b> of value <b>{{value}}</b> requested by {{by}} on <b>{{at}}</b>",
};
